import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery, useLocation } from '@common/hooks';
import { fullstorySetUser } from 'works/legacy/lib/fullstory';
import { bootIntercom, updateIntercom } from 'works/legacy/lib/intercom';
import { FeatureFlagsProvider } from '@common/contexts';
import { LoadingState } from '@common/components';
import { redirectToLogin } from 'works/legacy/lib/redirect-to-login';
import Auth from 'works/legacy/lib/auth';
import { setSentryMetadata } from 'works/legacy/lib/sentry';

const CURRENT_USER_INFO = gql`
  query currentUserInfo {
    currentMembership {
      id
      name
      role
      roleName
      active
      permissions
      entityType
      isSupplierUser
      saveLastVisitedPage
      canShowSidebarAdminItem {
        value
      }
      canShowSidebarBillingItem {
        value
      }
      canShowInvoices {
        value
      }
      canShowDisputes {
        value
      }
      worksUser {
        id
        firstName
        lastName
        initials
        email
        timeZone
        intercomUserHash
        internalTrustedUser
      }

      entity: entityProvider {
        ... on Organization {
          id
          name
          logoAvatarUrl
        }
        ... on Supplier {
          id
          name
        }
      }
    }
  }
`;

// @TODO: Refactor Works route architecture and remove this allow-list - WORK-8119
function isPublicRoute(path) {
  const PUBLIC_ROUTES_KEYS = [
    '/works/sign_in',
    '/works/auth/forgot-password',
    '/works/auth/reset-password/',
    '/works/auth/activate-account/',
    '/works/supplier/signup/',
  ];

  return PUBLIC_ROUTES_KEYS.some((authRouteKey) =>
    path?.includes(authRouteKey)
  );
}

export function AuthGuard({ children }) {
  const location = useLocation();
  const [isPublicPath, setIsPublicPath] = useState(
    isPublicRoute(location.pathname)
  );
  const { loading, error, data } = useQuery(CURRENT_USER_INFO, {
    // Query only when user is not on sign-in page.
    // Because it only query on mount.
    skip: isPublicPath,
  });

  // Update saveLastVisitedPage preference in Auth instance and local storage every time app is loaded
  useEffect(() => {
    if (data) {
      Auth.update({
        saveLastVisitedPage: data.currentMembership.saveLastVisitedPage,
      });
    }
  }, [data]);

  useEffect(() => {
    updateIntercom();
    setIsPublicPath(isPublicRoute(location.pathname));
  }, [location]);

  // Prevent private routes being rendered when user is on a few specific public routes
  if (isPublicPath) return null;

  if (loading) return <LoadingState />;

  // If this query does not succeed it's mostly likely because the session/token is
  // no longer valid. Send the user to the sign in page.
  if (error) {
    // Requires a hard refresh so that a new and valid csrf token is fetch
    // from the rails headers.
    // FIXME: this should be a simple router redirect if we fix the csrf
    // behavior.
    // Also fix spec/system/works/authentication_system_spec.rb:38.
    redirectToLogin();
    return null;
  }

  // Cache some info that is useful in multiple places in the app.
  if (data.currentMembership) {
    const worksMembership = data.currentMembership;
    const worksUser = worksMembership.worksUser;
    const entity = worksMembership.entity;

    setSentryMetadata(worksUser, entity, worksMembership);
    fullstorySetUser(worksUser, worksMembership);

    Auth.update({
      logo: entity.logoAvatarUrl || '',
      entityName: entity.name || '',
      name: worksMembership.name || '',
      membershipRole: worksMembership.roleName,
    });

    const dataUserAttributes = {
      user_id: worksUser.id,
      email: worksUser.email,
      name: `${worksUser.firstName} ${worksUser.lastName}`.trim(),
      first_name: worksUser.firstName,
      last_name: worksUser.lastName,
      membership_role: worksMembership.roleName,
      active: worksMembership.active,
      company: {
        company_id: entity.id,
        name: entity.name,
      },
      user_hash: worksUser.intercomUserHash,
    };

    bootIntercom(dataUserAttributes);
  }

  return <FeatureFlagsProvider>{children}</FeatureFlagsProvider>;
}

AuthGuard.propTypes = {
  children: PropTypes.node,
};
